import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Phone } from "lucide-react";
import { GALLERY_CATEGORIES, STOCK_GALLERY, BEFORE_AFTER, SITE } from "../lib/site";
import BeforeAfter from "../components/BeforeAfter";
import { getGallery } from "../lib/api";

export default function Gallery() {
  const [category, setCategory] = useState("all");
  const [uploaded, setUploaded] = useState([]);

  useEffect(() => {
    getGallery().then(setUploaded).catch(() => setUploaded([]));
  }, []);

  const items = useMemo(() => {
    const all = [...uploaded, ...STOCK_GALLERY];
    if (category === "all") return all;
    if (category === "before-after") return all.filter((i) => i.category === "before-after");
    return all.filter((i) => i.category === category);
  }, [category, uploaded]);

  return (
    <>
      <section className="section pt-16 md:pt-24" data-testid="gallery-hero">
        <div className="container-luxury">
          <p className="overline">Portfolio</p>
          <h1 className="mt-4 font-display text-5xl sm:text-6xl lg:text-7xl text-white font-light leading-[1.05] max-w-4xl">
            Recent work, <span className="font-script text-[var(--gold)]">recently finished</span>
          </h1>
          <p className="mt-6 text-white/60 text-lg max-w-2xl">
            A glimpse into the homes, exteriors and remodels we've completed in Wesley Chapel and
            surrounding areas.
          </p>
        </div>
      </section>

      {/* Before / After feature */}
      <section className="pb-12" data-testid="gallery-before-after">
        <div className="container-luxury">
          <div className="mb-8 flex items-center justify-between flex-wrap gap-4">
            <h2 className="font-display text-3xl text-white">Featured Transformation</h2>
            <span className="overline">Slide to compare</span>
          </div>
          <BeforeAfter before={BEFORE_AFTER.before} after={BEFORE_AFTER.after} />
        </div>
      </section>

      {/* Filters */}
      <section className="pb-8" data-testid="gallery-filters">
        <div className="container-luxury">
          <div className="flex flex-wrap gap-3 border-y border-white/5 py-6">
            {GALLERY_CATEGORIES.map((c) => (
              <button
                key={c.key}
                onClick={() => setCategory(c.key)}
                className={`px-4 py-2 text-xs uppercase tracking-[0.2em] border transition-colors ${
                  category === c.key
                    ? "border-[var(--gold)] text-[var(--gold)] bg-[var(--gold)]/5"
                    : "border-white/10 text-white/60 hover:text-white hover:border-white/30"
                }`}
                data-testid={`gallery-filter-${c.key}`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="pb-24" data-testid="gallery-grid">
        <div className="container-luxury">
          {items.length === 0 ? (
            <div className="text-center py-24 text-white/50">No projects in this category yet.</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/5">
              {items.map((it, idx) => (
                <div key={it.id || idx} className="bg-[#0A0A0A] group overflow-hidden" data-testid={`gallery-item-${idx}`}>
                  <div className="aspect-[4/3] overflow-hidden">
                    <img
                      src={it.image_url}
                      alt={it.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-6">
                    <p className="overline">{it.category.replace("-", " · ")}</p>
                    <h3 className="mt-2 font-display text-xl text-white">{it.title}</h3>
                    {it.description ? <p className="mt-2 text-white/55 text-sm">{it.description}</p> : null}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="section bg-[#0c0c0c]" data-testid="gallery-cta">
        <div className="container-luxury text-center">
          <h2 className="font-display text-4xl md:text-5xl text-white font-light">
            Your project could be <span className="font-script text-[var(--gold)]">next</span>.
          </h2>
          <div className="mt-8 flex flex-wrap gap-4 justify-center">
            <Link to="/contact" className="btn-primary" data-testid="gallery-estimate-cta">
              Request Free Estimate <ArrowRight size={16} />
            </Link>
            <a href={SITE.phoneHref} className="btn-secondary" data-testid="gallery-call-cta">
              <Phone size={16} /> {SITE.phone}
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
